/**
 * Add or edit an inspection type from Settings > Inspection types. The name,
 * description, what the type must be linked to, its safety check (a published
 * form filled before work starts) and whether the start waits on it, its
 * checklist (a published form filled on site), the result its checklist
 * suggests for a pass and a fail, what follows a fail, and who owns its
 * follow-up actions and when they are due are editable; the type's key is set
 * from its first name and never changes.
 */

import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { useFormTemplates } from '@/admin/hooks/use-forms'
import {
  type InspectionSubType,
  useActionOwnerLabel,
  useInspectionChoices,
  useSaveInspectionType,
} from '@/admin/hooks/use-inspections'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'
import { fieldErrorsFromApiError } from './member-attribute-helpers'

const FORM_ID = 'inspection-type-form'
// "No safety check" or "No checklist"; a select item cannot have an empty value.
const NO_FORM = 'none'
// "The tenant's first result for the outcome", the suggestion when none is named.
const FIRST_RESULT = 'first'
const OUTCOMES = ['pass', 'fail'] as const
// No level set: follow-up when a claim is linked, as before levels.
const AS_TODAY = 'as_today'
// "No one" owns a new action by default.
const NO_OWNER = 'none'

/** The form the type names as its safety check or checklist, or the "none" choice. */
function formOf(
  type: InspectionSubType | null,
  key: 'safety_template' | 'checklist_template',
): string {
  const named = type?.configuration_data?.[key]
  return typeof named === 'string' && named ? named : NO_FORM
}

/** The type's follow-up level, or the as-today choice. */
function followUpOf(type: InspectionSubType | null): string {
  const level = type?.configuration_data?.follow_up
  return typeof level === 'string' && level ? level : AS_TODAY
}

/** The fail results the type follows up; empty means every fail. */
function followUpOnOf(type: InspectionSubType | null): string[] {
  const on = type?.configuration_data?.follow_up_on
  return Array.isArray(on) ? on.filter((item): item is string => typeof item === 'string') : []
}

/** The party a new action is owned by, or the no-one choice. */
function actionOwnerOf(type: InspectionSubType | null): string {
  const owner = type?.configuration_data?.action_owner
  return typeof owner === 'string' && owner ? owner : NO_OWNER
}

/** The days a new action has until it is due, or blank for none. */
function actionDueDaysOf(type: InspectionSubType | null): number | '' {
  const days = type?.configuration_data?.action_due_days
  return typeof days === 'number' ? days : ''
}

/** The result the type's checklist suggests for ``outcome``, or the first-result choice. */
function suggestedOf(type: InspectionSubType | null, outcome: string): string {
  const named = (type?.configuration_data?.default_results as Record<string, unknown> | undefined)?.[
    outcome
  ]
  return typeof named === 'string' && named ? named : FIRST_RESULT
}

interface InspectionTypeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The type to edit; null adds a new one. */
  type: InspectionSubType | null
}

export function InspectionTypeDialog({
  open,
  onOpenChange,
  type,
}: InspectionTypeDialogProps) {
  const { t } = useTranslation()
  const ownerLabel = useActionOwnerLabel()
  const save = useSaveInspectionType()
  // Only a published form can be filled; a draft never is.
  const formsQuery = useFormTemplates({ activeOnly: true, publishedOnly: true })
  const forms = useMemo(() => formsQuery.data ?? [], [formsQuery.data])
  const { data: choices, isPending: choicesPending } = useInspectionChoices()
  // The pickers' options load before the form shows: options arriving later
  // would rebuild the fields and close a picker under the admin's hand.
  const ready = !formsQuery.isPending && !choicesPending
  // The levels that send a fail on to follow-up, as the platform says.
  const followingLevels = useMemo(
    () =>
      new Set(
        (choices?.follow_up_levels ?? [])
          .filter((level) => level.follows_up)
          .map((level) => level.value),
      ),
    [choices],
  )
  // Any level but the ones that never follow up, as-today included.
  const mayFollowUp = useCallback(
    (level: string) => level === AS_TODAY || followingLevels.has(level),
    [followingLevels],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: t('inspectionTypes.nameLabel', 'Name'),
        placeholder: t('inspectionTypes.namePlaceholder', 'e.g. Site survey'),
        value: '',
        autoFocus: true,
        required: t('inspectionTypes.nameRequired', 'Name is required'),
      },
      {
        type: 'textarea',
        name: 'description',
        label: t('inspectionTypes.descriptionLabel', 'Description'),
        value: '',
      },
      {
        type: 'checkbox',
        name: 'requires_claim',
        label: t('inspectionTypes.requiresClaimLabel', 'Must be linked to a {{claimNounLower}}'),
        value: false,
      },
      {
        type: 'checkbox',
        name: 'requires_holding',
        label: t(
          'inspectionTypes.requiresHoldingLabel',
          'Must be linked to a {{holdingNounLower}}',
        ),
        value: false,
      },
      {
        type: 'select',
        name: 'safety_template',
        label: t('inspectionTypes.safetyLabel', 'Safety check'),
        hint: t(
          'inspectionTypes.safetyHint',
          'The form the inspector fills before work starts. Build one in Settings, Forms.',
        ),
        value: NO_FORM,
        options: [
          { value: NO_FORM, label: t('inspectionTypes.noSafety', 'No safety check') },
          ...forms.map((form) => ({ value: form.key, label: form.name })),
        ],
        configurationData: { searchable: true },
        testId: 'inspection-type-safety',
      },
      {
        type: 'checkbox',
        name: 'safety_blocks_start',
        label: t(
          'inspectionTypes.safetyBlocksStartLabel',
          'Must be done and safe before the inspection starts',
        ),
        value: false,
        visible: (ctx) => ctx.values.safety_template !== NO_FORM,
        testId: 'inspection-type-safety-blocks-start',
      },
      {
        type: 'select',
        name: 'checklist_template',
        label: t('inspectionTypes.checklistLabel', 'Checklist'),
        hint: t(
          'inspectionTypes.checklistHint',
          'The form the inspector fills on site. Build one in Settings, Forms.',
        ),
        value: NO_FORM,
        options: [
          { value: NO_FORM, label: t('inspectionTypes.noChecklist', 'No checklist') },
          ...forms.map((form) => ({ value: form.key, label: form.name })),
        ],
        configurationData: { searchable: true },
        testId: 'inspection-type-checklist',
      },
      // What the checklist's score suggests in Record Result; only a type
      // with a checklist has one.
      ...OUTCOMES.map((outcome): FormField => {
        const results = (choices?.results ?? []).filter((r) => r.outcome === outcome)
        return {
          type: 'select',
          name: `suggest_${outcome}`,
          label:
            outcome === 'pass'
              ? t('inspectionTypes.suggestPassLabel', 'Suggested result for a pass')
              : t('inspectionTypes.suggestFailLabel', 'Suggested result for a fail'),
          hint:
            outcome === 'fail'
              ? t(
                  'inspectionTypes.suggestHint',
                  "Record Result starts on it when the checklist's score points that way. The inspector can change it.",
                )
              : undefined,
          value: FIRST_RESULT,
          options: [
            {
              value: FIRST_RESULT,
              label: t('inspectionTypes.suggestFirst', 'Default ({{result}})', {
                result: results[0]?.label ?? '',
              }),
            },
            ...results.map((r) => ({ value: r.value, label: r.label })),
          ],
          visible: (ctx) => ctx.values.checklist_template !== NO_FORM,
          testId: `inspection-type-suggest-${outcome}`,
        }
      }),
      {
        type: 'select',
        name: 'follow_up',
        label: t('inspectionTypes.followUpLabel', 'After a fail'),
        hint: t(
          'inspectionTypes.followUpHint',
          'Whether a failing result goes on to follow-up work.',
        ),
        value: AS_TODAY,
        options: [
          {
            value: AS_TODAY,
            label: t('inspectionTypes.followUpAsToday', 'Repair tracking when a claim is linked'),
          },
          ...(choices?.follow_up_levels ?? []).map((level) => ({
            value: level.value,
            label: level.label,
          })),
        ],
        testId: 'inspection-type-follow-up',
      },
      {
        type: 'multiSelect',
        name: 'follow_up_on',
        label: t('inspectionTypes.followUpOnLabel', 'Follow up these fails'),
        hint: t('inspectionTypes.followUpOnHint', 'None picked means every fail.'),
        value: [],
        options: (choices?.results ?? [])
          .filter((result) => result.outcome === 'fail')
          .map((result) => ({ value: result.value, label: result.label })),
        chips: true,
        visible: (ctx) => followingLevels.has(String(ctx.values.follow_up)),
        testId: 'inspection-type-follow-up-on',
      },
      // A fail can go to follow-up unless the level says it never does.
      {
        type: 'select',
        name: 'action_owner',
        label: t('inspectionTypes.actionOwnerLabel', 'Actions are owned by'),
        hint: t(
          'inspectionTypes.actionOwnerHint',
          'Who each new action goes to. It can be changed on the action.',
        ),
        value: NO_OWNER,
        options: [
          { value: NO_OWNER, label: t('inspectionTypes.noActionOwner', 'No one') },
          ...(choices?.action_owners ?? [])
            .filter((owner) => owner.party)
            .map((owner) => ({ value: owner.value, label: ownerLabel(owner.value, owner.label) })),
        ],
        visible: (ctx) => mayFollowUp(String(ctx.values.follow_up)),
        testId: 'inspection-type-action-owner',
      },
      {
        type: 'number',
        name: 'action_due_days',
        label: t('inspectionTypes.actionDueDaysLabel', 'Actions are due after (days)'),
        hint: t('inspectionTypes.actionDueDaysHint', 'Blank means no due date.'),
        value: '',
        visible: (ctx) => mayFollowUp(String(ctx.values.follow_up)),
        testId: 'inspection-type-action-due-days',
      },
      // When the follow-up reminders go out; a blank one is not sent.
      ...[
        t('inspectionTypes.reminderFirstLabel', 'First reminder (days after the fail)'),
        t('inspectionTypes.reminderSecondLabel', 'Second reminder (days after the fail)'),
        t('inspectionTypes.reminderLastLabel', 'Last reminder (days after the fail)'),
      ].map(
        (label, index): FormField => ({
          type: 'number',
          name: `reminder_${index + 1}`,
          label,
          hint:
            index === 0
              ? t(
                  'inspectionTypes.reminderHint',
                  'Each one after the one before. Leave one blank to send fewer.',
                )
              : undefined,
          value: '',
          visible: (ctx) => mayFollowUp(String(ctx.values.follow_up)),
          testId: `inspection-type-reminder-${index + 1}`,
        }),
      ),
    ],
    [forms, choices, followingLevels, mayFollowUp, ownerLabel, t],
  )

  // A new type starts on the platform's reminder days.
  const defaultReminders = choices?.default_reminder_days

  const { Form, ref } = useForm(
    { name: FORM_ID, submit: 'footer', fields },
    async (values, formCtx) => {
      const v = values as {
        label?: string
        description?: string
        requires_claim?: boolean
        requires_holding?: boolean
        safety_template?: string
        safety_blocks_start?: boolean
        checklist_template?: string
        suggest_pass?: string
        suggest_fail?: string
        follow_up?: string
        follow_up_on?: string[]
        action_owner?: string
        action_due_days?: number | string | null
        reminder_1?: number | string | null
        reminder_2?: number | string | null
        reminder_3?: number | string | null
      }
      const safety = String(v.safety_template ?? NO_FORM)
      const checklist = String(v.checklist_template ?? NO_FORM)
      const suggested = (value?: string) => (value && value !== FIRST_RESULT ? value : '')
      // Reminder days are sent only when changed, so a type left on the
      // default keeps following it.
      const reminders = [v.reminder_1, v.reminder_2, v.reminder_3]
        .filter((day) => day !== '' && day != null)
        .map(Number)
      const remindersBefore = type?.reminder_days ?? defaultReminders ?? []
      try {
        await save.mutateAsync({
          uuid: type?.uuid,
          label: String(v.label ?? '').trim(),
          description: String(v.description ?? '').trim(),
          requires_claim: Boolean(v.requires_claim),
          requires_holding: Boolean(v.requires_holding),
          safety_template: safety === NO_FORM ? '' : safety,
          safety_blocks_start: safety !== NO_FORM && Boolean(v.safety_blocks_start),
          checklist_template: checklist === NO_FORM ? '' : checklist,
          default_results: {
            pass: suggested(v.suggest_pass),
            fail: suggested(v.suggest_fail),
          },
          follow_up: !v.follow_up || v.follow_up === AS_TODAY ? '' : v.follow_up,
          follow_up_on: v.follow_up_on ?? [],
          action_owner: !v.action_owner || v.action_owner === NO_OWNER ? '' : v.action_owner,
          action_due_days:
            v.action_due_days === '' || v.action_due_days == null
              ? null
              : Number(v.action_due_days),
          ...(reminders.join() === remindersBefore.join() ? {} : { reminder_days: reminders }),
        })
        toast.success(
          type
            ? t('inspectionTypes.saved', 'Inspection type saved.')
            : t('inspectionTypes.added', 'Inspection type added.'),
        )
        onOpenChange(false)
      } catch (err) {
        const fieldErrors = fieldErrorsFromApiError(err)
        if (fieldErrors.label) {
          formCtx.setFieldError('label', fieldErrors.label)
        } else if (fieldErrors.safety_template) {
          formCtx.setFieldError('safety_template', fieldErrors.safety_template)
        } else if (fieldErrors.checklist_template) {
          formCtx.setFieldError('checklist_template', fieldErrors.checklist_template)
        } else if (fieldErrors.action_due_days) {
          formCtx.setFieldError('action_due_days', fieldErrors.action_due_days)
        } else if (fieldErrors.reminder_days) {
          formCtx.setFieldError('reminder_1', fieldErrors.reminder_days)
        } else {
          toastApiError(
            err,
            t('inspectionTypes.saveFailed', 'Could not save the inspection type.'),
          )
        }
      }
    },
    [type, save, onOpenChange, defaultReminders, t],
  )

  // Seed the form with the type being edited (or blanks) each time it opens,
  // once its pickers have loaded.
  useEffect(() => {
    if (open && ready) {
      const reminders = type?.reminder_days ?? defaultReminders ?? []
      ref.current?.resetForm({
        values: {
          label: type?.label ?? '',
          description: type?.description ?? '',
          requires_claim: type?.requires_claim ?? false,
          requires_holding: type?.requires_holding ?? false,
          safety_template: formOf(type, 'safety_template'),
          safety_blocks_start: type?.configuration_data?.safety_blocks_start === true,
          checklist_template: formOf(type, 'checklist_template'),
          suggest_pass: suggestedOf(type, 'pass'),
          suggest_fail: suggestedOf(type, 'fail'),
          follow_up: followUpOf(type),
          follow_up_on: followUpOnOf(type),
          action_owner: actionOwnerOf(type),
          action_due_days: actionDueDaysOf(type),
          reminder_1: reminders[0] ?? '',
          reminder_2: reminders[1] ?? '',
          reminder_3: reminders[2] ?? '',
        },
      })
    }
  }, [open, ready, type, ref, defaultReminders])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[15px] text-[var(--ink)]">
            {type
              ? t('inspectionTypes.editTitle', 'Edit inspection type')
              : t('inspectionTypes.addTitle', 'Add inspection type')}
          </DialogTitle>
        </DialogHeader>

        {ready ? (
          <Form className="space-y-4" />
        ) : (
          <div className="flex justify-center py-8" data-testid="inspection-type-loading">
            <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
          </div>
        )}

        <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {/* Nothing to submit until the form shows, once its pickers load. */}
          <Button type="submit" form={FORM_ID} disabled={save.isPending || !ready}>
            {save.isPending && <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />}
            {type
              ? t('common.saveChanges', 'Save changes')
              : t('inspectionTypes.add', 'Add type')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
